import React, { useMemo } from "react";
import { DetectionZoneCardinalDirection, TurnType } from "@app/shared";
import { LeftTurnArrow, RightTurnArrow, StraightArrow, UTurnArrow } from "./TurnArrows";

interface IntersectionDiagramProps {
    reportColumns: any[];
    hourlyData: Map<string, number>;
    dailyTotals: Map<string, number>;
    columnsByDirection: Map<number, any[]>;
    directionStreetNames: Map<number, string>;
    // When true the SVG scales to its container and the legend is hidden, so
    // several diagrams can sit side by side (used by the by-class report).
    compact?: boolean;
}

export const IntersectionDiagram: React.FC<IntersectionDiagramProps> = ({ reportColumns, hourlyData, dailyTotals, columnsByDirection, directionStreetNames, compact = false }) => {
    const width = 1100;
    const height = 1000;
    const centerX = width / 2;
    const centerY = height / 2;

    const intersectionSize = 300;
    const laneWidth = 60;
    const roadLength = 300;

    const isDiagonal = useMemo(() => {
        const directions = Array.from(columnsByDirection.keys());
        return directions.some((dir) => [45, 135, 225, 315].includes(dir));
    }, [columnsByDirection]);

    const directionMapping = useMemo(() => {
        if (!isDiagonal) {
            return {
                [DetectionZoneCardinalDirection.North]: DetectionZoneCardinalDirection.North,
                [DetectionZoneCardinalDirection.East]: DetectionZoneCardinalDirection.East,
                [DetectionZoneCardinalDirection.South]: DetectionZoneCardinalDirection.South,
                [DetectionZoneCardinalDirection.West]: DetectionZoneCardinalDirection.West,
            };
        }
        return {
            [DetectionZoneCardinalDirection.NorthEast]: DetectionZoneCardinalDirection.North,
            [DetectionZoneCardinalDirection.SouthEast]: DetectionZoneCardinalDirection.East,
            [DetectionZoneCardinalDirection.SouthWest]: DetectionZoneCardinalDirection.South,
            [DetectionZoneCardinalDirection.NorthWest]: DetectionZoneCardinalDirection.West,
        };
    }, [isDiagonal]);

    const getCount = (direction: DetectionZoneCardinalDirection, turnType?: TurnType): string => {
        const column = reportColumns.find((col) => col.direction === direction && (turnType === undefined || col.turnType === turnType));
        if (!column || !column.key) return "0";

        const daily = dailyTotals.get(column.key) || 0;
        const hourly = hourlyData.get(column.key);

        if (hourly !== undefined && hourlyData.size > 0) {
            return `${daily} (${hourly})`;
        }
        return daily.toString();
    };

    const getTurnTypeToDestination = (fromDir: number, toDir: number): TurnType | null => {
        let diff = (toDir - fromDir - 180 + 360 + 360) % 360;

        if (diff === 0) return TurnType.Straight;
        if (diff === 90) return TurnType.Right;
        if (diff === 270) return TurnType.Left;

        return null;
    };

    const getDirectionTotal = (direction: DetectionZoneCardinalDirection): string => {
        const allDirections = Array.from(columnsByDirection.keys());
        let dailySum = 0;
        let hourlySum = 0;

        allDirections.forEach((sourceDir) => {
            if (sourceDir === direction) return;

            const turnType = getTurnTypeToDestination(sourceDir, direction);
            if (turnType === null) return;

            const column = reportColumns.find((col) => col.direction === sourceDir && col.turnType === turnType);
            if (!column || !column.key) return;

            dailySum += dailyTotals.get(column.key) || 0;
            const hourly = hourlyData.get(column.key);
            if (hourly !== undefined) {
                hourlySum += hourly;
            }
        });

        if (hourlyData.size > 0) {
            return `${dailySum} (${hourlySum})`;
        }
        return dailySum.toString();
    };

    const getStreetName = (direction: DetectionZoneCardinalDirection): string => {
        return directionStreetNames.get(direction) || "";
    };

    const getCardinalAbbr = (direction: DetectionZoneCardinalDirection): string => {
        const abbrs: Record<number, string> = {
            [DetectionZoneCardinalDirection.North]: "NB",
            [DetectionZoneCardinalDirection.NorthEast]: "NEB",
            [DetectionZoneCardinalDirection.East]: "EB",
            [DetectionZoneCardinalDirection.SouthEast]: "SEB",
            [DetectionZoneCardinalDirection.South]: "SB",
            [DetectionZoneCardinalDirection.SouthWest]: "SWB",
            [DetectionZoneCardinalDirection.West]: "WB",
            [DetectionZoneCardinalDirection.NorthWest]: "NWB",
        };
        return abbrs[direction] || "";
    };

    const getMappedDirection = (standardDir: DetectionZoneCardinalDirection): DetectionZoneCardinalDirection | null => {
        const availableDirections = Array.from(columnsByDirection.keys());

        if (availableDirections.includes(standardDir)) {
            return standardDir;
        }

        for (const dir of availableDirections) {
            if ((directionMapping as any)[dir] === standardDir) {
                return dir;
            }
        }

        return null;
    };

    const renderRoad = (isVertical: boolean) => {
        const elements: JSX.Element[] = [];

        const intersectionOffset = isDiagonal ? (Math.sqrt(2) * intersectionSize) / 2 : intersectionSize / 2;

        const borderLineExtension = isDiagonal ? 88 : 0;

        if (isVertical) {
            const roadLeft = centerX - laneWidth * 1.5;

            const southDir = getMappedDirection(DetectionZoneCardinalDirection.South);
            const northDir = getMappedDirection(DetectionZoneCardinalDirection.North);

            if (southDir === null && northDir === null) {
                return elements;
            }

            const southLeftCount = southDir !== null ? getCount(southDir, TurnType.Left) : "0";
            const southStraightCount = southDir !== null ? getCount(southDir, TurnType.Straight) : "0";
            const southRightCount = southDir !== null ? getCount(southDir, TurnType.Right) : "0";
            const southUTurnCount = southDir !== null ? getCount(southDir, TurnType.UTurn) : "0";
            const southOutCount = southDir !== null ? getDirectionTotal(southDir) : "0";
            const southStreet = southDir !== null ? getStreetName(southDir) : "";
            const southCardinal = southDir !== null ? getCardinalAbbr(southDir) : "";

            const northLeftCount = northDir !== null ? getCount(northDir, TurnType.Left) : "0";
            const northStraightCount = northDir !== null ? getCount(northDir, TurnType.Straight) : "0";
            const northRightCount = northDir !== null ? getCount(northDir, TurnType.Right) : "0";
            const northUTurnCount = northDir !== null ? getCount(northDir, TurnType.UTurn) : "0";
            const northOutCount = northDir !== null ? getDirectionTotal(northDir) : "0";
            const northStreet = northDir !== null ? getStreetName(northDir) : "";
            const northCardinal = northDir !== null ? getCardinalAbbr(northDir) : "";

            if (southDir !== null) {
                elements.push(<line key="divider-south-center" x1={centerX} y1={centerY - intersectionOffset - roadLength} x2={centerX} y2={centerY - intersectionOffset} stroke="#333333" strokeWidth="2" strokeDasharray="10,10" />);
                elements.push(<line key="border-south-left" x1={roadLeft} y1={centerY - intersectionOffset - roadLength} x2={roadLeft} y2={centerY - intersectionOffset + borderLineExtension} stroke="#333333" strokeWidth="2" />);
                elements.push(<line key="border-south-right" x1={roadLeft + laneWidth * 3} y1={centerY - intersectionOffset - roadLength} x2={roadLeft + laneWidth * 3} y2={centerY - intersectionOffset + borderLineExtension} stroke="#333333" strokeWidth="2" />);
            }

            if (northDir !== null) {
                elements.push(<line key="divider-north-center" x1={centerX} y1={centerY + intersectionOffset} x2={centerX} y2={centerY + intersectionOffset + roadLength} stroke="#333333" strokeWidth="2" strokeDasharray="10,10" />);
                elements.push(<line key="border-north-left" x1={roadLeft} y1={centerY + intersectionOffset - borderLineExtension} x2={roadLeft} y2={centerY + intersectionOffset + roadLength} stroke="#333333" strokeWidth="2" />);
                elements.push(<line key="border-north-right" x1={roadLeft + laneWidth * 3} y1={centerY + intersectionOffset - borderLineExtension} x2={roadLeft + laneWidth * 3} y2={centerY + intersectionOffset + roadLength} stroke="#333333" strokeWidth="2" />);
            }

            if (southDir !== null) {
                const southArrowX = centerX - laneWidth;
                const southArrowY = centerY - intersectionOffset - 80;
                elements.push(<path key="arrow-south-in" d={`M ${southArrowX},${southArrowY} L ${southArrowX - 10},${southArrowY - 20} L ${southArrowX - 4},${southArrowY - 20} L ${southArrowX - 4},${southArrowY - 30} L ${southArrowX + 4},${southArrowY - 30} L ${southArrowX + 4},${southArrowY - 20} L ${southArrowX + 10},${southArrowY - 20} Z`} fill="#333333" stroke="#333333" strokeWidth="1" />);

                const southTurnY = centerY - intersectionOffset - 150;
                const southTurnXOffset = laneWidth * 1.8;
                const southTurnArrowX = centerX - southTurnXOffset;
                const southTextX = southTurnArrowX - 25;
                elements.push(
                    <g key="south-left-turn" transform={`translate(${southTurnArrowX}, ${southTurnY - 75})`}>
                        <LeftTurnArrow rotation={180} size={35} />
                    </g>
                );
                elements.push(
                    <text key="south-left-count" x={southTextX} y={southTurnY - 75 + 5} textAnchor="end" fontSize="14" fontWeight="bold" fill="#2c3e50">
                        {southLeftCount}
                    </text>
                );
                // Straight
                elements.push(
                    <g key="south-straight-turn" transform={`translate(${southTurnArrowX}, ${southTurnY - 25})`}>
                        <StraightArrow rotation={180} size={35} />
                    </g>
                );
                elements.push(
                    <text key="south-straight-count" x={southTextX} y={southTurnY - 25 + 5} textAnchor="end" fontSize="14" fontWeight="bold" fill="#2c3e50">
                        {southStraightCount}
                    </text>
                );
                // Right turn
                elements.push(
                    <g key="south-right-turn" transform={`translate(${southTurnArrowX}, ${southTurnY + 25})`}>
                        <RightTurnArrow rotation={180} size={35} />
                    </g>
                );
                elements.push(
                    <text key="south-right-count" x={southTextX} y={southTurnY + 25 + 5} textAnchor="end" fontSize="14" fontWeight="bold" fill="#2c3e50">
                        {southRightCount}
                    </text>
                );
                // U-turn
                elements.push(
                    <g key="south-uturn" transform={`translate(${southTurnArrowX}, ${southTurnY + 75})`}>
                        <UTurnArrow rotation={180} size={35} />
                    </g>
                );
                elements.push(
                    <text key="south-uturn-count" x={southTextX} y={southTurnY + 75 + 5} textAnchor="end" fontSize="14" fontWeight="bold" fill="#2c3e50">
                        {southUTurnCount}
                    </text>
                );

                const southOutArrowX = centerX + laneWidth;
                const southOutArrowY = centerY - intersectionOffset - 80;
                elements.push(<path key="arrow-south-out" d={`M ${southOutArrowX},${southOutArrowY - 30} L ${southOutArrowX - 10},${southOutArrowY - 10} L ${southOutArrowX - 4},${southOutArrowY - 10} L ${southOutArrowX - 4},${southOutArrowY} L ${southOutArrowX + 4},${southOutArrowY} L ${southOutArrowX + 4},${southOutArrowY - 10} L ${southOutArrowX + 10},${southOutArrowY - 10} Z`} fill="#dc2626" stroke="#dc2626" strokeWidth="1" />);
                elements.push(
                    <text key="south-out-label" x={southOutArrowX} y={southOutArrowY - 40} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#dc2626">
                        {southOutCount}
                    </text>
                );

                elements.push(
                    <text key="south-street-name" x={centerX} y={centerY - intersectionOffset - roadLength - 10} textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333333">
                        {southStreet}
                    </text>
                );

                elements.push(
                    <text key="south-cardinal" x={centerX - laneWidth} y={centerY - intersectionOffset - roadLength + 20} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333333">
                        {southCardinal}
                    </text>
                );
            }

            if (northDir !== null) {
                const northArrowX = centerX + laneWidth;
                const northArrowY = centerY + intersectionOffset + 80;
                elements.push(<path key="arrow-north-in" d={`M ${northArrowX},${northArrowY} L ${northArrowX - 10},${northArrowY + 20} L ${northArrowX - 4},${northArrowY + 20} L ${northArrowX - 4},${northArrowY + 30} L ${northArrowX + 4},${northArrowY + 30} L ${northArrowX + 4},${northArrowY + 20} L ${northArrowX + 10},${northArrowY + 20} Z`} fill="#333333" stroke="#333333" strokeWidth="1" />);

                const northTurnY = centerY + intersectionOffset + 150;
                const northTurnXOffset = laneWidth * 1.8;
                const northTurnArrowX = centerX + northTurnXOffset;
                const northTextX = northTurnArrowX + 25;
                elements.push(
                    <g key="north-left-turn" transform={`translate(${northTurnArrowX}, ${northTurnY + 75})`}>
                        <LeftTurnArrow rotation={0} size={35} />
                    </g>
                );
                elements.push(
                    <text key="north-left-count" x={northTextX} y={northTurnY + 75 + 5} textAnchor="start" fontSize="14" fontWeight="bold" fill="#2c3e50">
                        {northLeftCount}
                    </text>
                );
                // Straight
                elements.push(
                    <g key="north-straight-turn" transform={`translate(${northTurnArrowX}, ${northTurnY + 25})`}>
                        <StraightArrow rotation={0} size={35} />
                    </g>
                );
                elements.push(
                    <text key="north-straight-count" x={northTextX} y={northTurnY + 25 + 5} textAnchor="start" fontSize="14" fontWeight="bold" fill="#2c3e50">
                        {northStraightCount}
                    </text>
                );
                // Right turn
                elements.push(
                    <g key="north-right-turn" transform={`translate(${northTurnArrowX}, ${northTurnY - 25})`}>
                        <RightTurnArrow rotation={0} size={35} />
                    </g>
                );
                elements.push(
                    <text key="north-right-count" x={northTextX} y={northTurnY - 25 + 5} textAnchor="start" fontSize="14" fontWeight="bold" fill="#2c3e50">
                        {northRightCount}
                    </text>
                );
                // U-turn
                elements.push(
                    <g key="north-uturn" transform={`translate(${northTurnArrowX}, ${northTurnY - 75})`}>
                        <UTurnArrow rotation={0} size={35} />
                    </g>
                );
                elements.push(
                    <text key="north-uturn-count" x={northTextX} y={northTurnY - 75 + 5} textAnchor="start" fontSize="14" fontWeight="bold" fill="#2c3e50">
                        {northUTurnCount}
                    </text>
                );

                const northOutArrowX = centerX - laneWidth;
                const northOutArrowY = centerY + intersectionOffset + 80;
                elements.push(<path key="arrow-north-out" d={`M ${northOutArrowX},${northOutArrowY + 30} L ${northOutArrowX - 10},${northOutArrowY + 10} L ${northOutArrowX - 4},${northOutArrowY + 10} L ${northOutArrowX - 4},${northOutArrowY} L ${northOutArrowX + 4},${northOutArrowY} L ${northOutArrowX + 4},${northOutArrowY + 10} L ${northOutArrowX + 10},${northOutArrowY + 10} Z`} fill="#dc2626" stroke="#dc2626" strokeWidth="1" />);
                elements.push(
                    <text key="north-out-label" x={northOutArrowX} y={northOutArrowY + 50} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#dc2626">
                        {northOutCount}
                    </text>
                );

                elements.push(
                    <text key="north-street-name" x={centerX} y={centerY + intersectionOffset + roadLength + 20} textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333333">
                        {northStreet}
                    </text>
                );

                elements.push(
                    <text key="north-cardinal" x={centerX + laneWidth} y={centerY + intersectionOffset + roadLength - 10} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333333">
                        {northCardinal}
                    </text>
                );
            }
        } else {
            const roadTop = centerY - laneWidth * 1.5;

            const eastDir = getMappedDirection(DetectionZoneCardinalDirection.East);
            const westDir = getMappedDirection(DetectionZoneCardinalDirection.West);

            if (eastDir === null && westDir === null) {
                return elements;
            }

            const eastLeftCount = eastDir !== null ? getCount(eastDir, TurnType.Left) : "0";
            const eastStraightCount = eastDir !== null ? getCount(eastDir, TurnType.Straight) : "0";
            const eastRightCount = eastDir !== null ? getCount(eastDir, TurnType.Right) : "0";
            const eastUTurnCount = eastDir !== null ? getCount(eastDir, TurnType.UTurn) : "0";
            const eastOutCount = eastDir !== null ? getDirectionTotal(eastDir) : "0";
            const eastStreet = eastDir !== null ? getStreetName(eastDir) : "";
            const eastCardinal = eastDir !== null ? getCardinalAbbr(eastDir) : "";

            const westLeftCount = westDir !== null ? getCount(westDir, TurnType.Left) : "0";
            const westStraightCount = westDir !== null ? getCount(westDir, TurnType.Straight) : "0";
            const westRightCount = westDir !== null ? getCount(westDir, TurnType.Right) : "0";
            const westUTurnCount = westDir !== null ? getCount(westDir, TurnType.UTurn) : "0";
            const westOutCount = westDir !== null ? getDirectionTotal(westDir) : "0";
            const westStreet = westDir !== null ? getStreetName(westDir) : "";
            const westCardinal = westDir !== null ? getCardinalAbbr(westDir) : "";

            if (eastDir !== null) {
                elements.push(<line key="divider-east-center" x1={centerX - intersectionOffset - roadLength} y1={centerY} x2={centerX - intersectionOffset} y2={centerY} stroke="#333333" strokeWidth="2" strokeDasharray="10,10" />);
                elements.push(<line key="border-east-top" x1={centerX - intersectionOffset - roadLength} y1={roadTop} x2={centerX - intersectionOffset + borderLineExtension} y2={roadTop} stroke="#333333" strokeWidth="2" />);
                elements.push(<line key="border-east-bottom" x1={centerX - intersectionOffset - roadLength} y1={roadTop + laneWidth * 3} x2={centerX - intersectionOffset + borderLineExtension} y2={roadTop + laneWidth * 3} stroke="#333333" strokeWidth="2" />);
            }

            if (westDir !== null) {
                elements.push(<line key="divider-west-center" x1={centerX + intersectionOffset} y1={centerY} x2={centerX + intersectionOffset + roadLength} y2={centerY} stroke="#333333" strokeWidth="2" strokeDasharray="10,10" />);
                elements.push(<line key="border-west-top" x1={centerX + intersectionOffset - borderLineExtension} y1={roadTop} x2={centerX + intersectionOffset + roadLength} y2={roadTop} stroke="#333333" strokeWidth="2" />);
                elements.push(<line key="border-west-bottom" x1={centerX + intersectionOffset - borderLineExtension} y1={roadTop + laneWidth * 3} x2={centerX + intersectionOffset + roadLength} y2={roadTop + laneWidth * 3} stroke="#333333" strokeWidth="2" />);
            }

            // === EAST DIRECTION RENDERING ===
            if (eastDir !== null) {
                const eastArrowX = centerX - intersectionOffset - 80;
                const eastArrowY = centerY + laneWidth;
                elements.push(<path key="arrow-east-in" d={`M ${eastArrowX},${eastArrowY} L ${eastArrowX - 20},${eastArrowY - 10} L ${eastArrowX - 20},${eastArrowY - 4} L ${eastArrowX - 30},${eastArrowY - 4} L ${eastArrowX - 30},${eastArrowY + 4} L ${eastArrowX - 20},${eastArrowY + 4} L ${eastArrowX - 20},${eastArrowY + 10} Z`} fill="#333333" stroke="#333333" strokeWidth="1" />);

                const eastTurnX = centerX - intersectionOffset - 150;
                const eastTurnYOffset = laneWidth * 1.8;
                const eastTurnArrowY = centerY + eastTurnYOffset;
                const eastTextY = eastTurnArrowY + 25; // Text BELOW arrows (RIGHT of eastbound traffic)
                elements.push(
                    <g key="east-left-turn" transform={`translate(${eastTurnX - 75}, ${eastTurnArrowY})`}>
                        <LeftTurnArrow rotation={90} size={35} />
                    </g>
                );
                elements.push(
                    <text key="east-left-count" x={eastTurnX - 75} y={eastTextY} textAnchor="end" fontSize="14" fontWeight="bold" fill="#2c3e50" transform={`rotate(-90, ${eastTurnX - 75}, ${eastTextY})`}>
                        {eastLeftCount}
                    </text>
                );
                // Straight
                elements.push(
                    <g key="east-straight-turn" transform={`translate(${eastTurnX - 25}, ${eastTurnArrowY})`}>
                        <StraightArrow rotation={90} size={35} />
                    </g>
                );
                elements.push(
                    <text key="east-straight-count" x={eastTurnX - 25} y={eastTextY} textAnchor="end" fontSize="14" fontWeight="bold" fill="#2c3e50" transform={`rotate(-90, ${eastTurnX - 25}, ${eastTextY})`}>
                        {eastStraightCount}
                    </text>
                );
                // Right turn
                elements.push(
                    <g key="east-right-turn" transform={`translate(${eastTurnX + 25}, ${eastTurnArrowY})`}>
                        <RightTurnArrow rotation={90} size={35} />
                    </g>
                );
                elements.push(
                    <text key="east-right-count" x={eastTurnX + 25} y={eastTextY} textAnchor="end" fontSize="14" fontWeight="bold" fill="#2c3e50" transform={`rotate(-90, ${eastTurnX + 25}, ${eastTextY})`}>
                        {eastRightCount}
                    </text>
                );
                // U-turn
                elements.push(
                    <g key="east-uturn" transform={`translate(${eastTurnX + 75}, ${eastTurnArrowY})`}>
                        <UTurnArrow rotation={90} size={35} />
                    </g>
                );
                elements.push(
                    <text key="east-uturn-count" x={eastTurnX + 75} y={eastTextY} textAnchor="end" fontSize="14" fontWeight="bold" fill="#2c3e50" transform={`rotate(-90, ${eastTurnX + 75}, ${eastTextY})`}>
                        {eastUTurnCount}
                    </text>
                );

                const eastOutArrowX = centerX - intersectionOffset - 80;
                const eastOutArrowY = centerY - laneWidth;
                elements.push(<path key="arrow-east-out" d={`M ${eastOutArrowX - 30},${eastOutArrowY} L ${eastOutArrowX - 10},${eastOutArrowY - 10} L ${eastOutArrowX - 10},${eastOutArrowY - 4} L ${eastOutArrowX},${eastOutArrowY - 4} L ${eastOutArrowX},${eastOutArrowY + 4} L ${eastOutArrowX - 10},${eastOutArrowY + 4} L ${eastOutArrowX - 10},${eastOutArrowY + 10} Z`} fill="#dc2626" stroke="#dc2626" strokeWidth="1" />);
                elements.push(
                    <text key="east-out-label" x={eastOutArrowX - 45} y={eastOutArrowY + 5} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#dc2626" transform={`rotate(-90, ${eastOutArrowX - 45}, ${eastOutArrowY + 5})`}>
                        {eastOutCount}
                    </text>
                );

                elements.push(
                    <text key="east-street-name" x={centerX - intersectionOffset - roadLength - 10} y={centerY} textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333333" transform={`rotate(-90, ${centerX - intersectionOffset - roadLength - 10}, ${centerY})`}>
                        {eastStreet}
                    </text>
                );

                elements.push(
                    <text key="east-cardinal" x={centerX - intersectionOffset - roadLength + 20} y={centerY + laneWidth} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333333">
                        {eastCardinal}
                    </text>
                );
            }

            if (westDir !== null) {
                const westArrowX = centerX + intersectionOffset + 80;
                const westArrowY = centerY - laneWidth;
                elements.push(<path key="arrow-west-in" d={`M ${westArrowX},${westArrowY} L ${westArrowX + 20},${westArrowY - 10} L ${westArrowX + 20},${westArrowY - 4} L ${westArrowX + 30},${westArrowY - 4} L ${westArrowX + 30},${westArrowY + 4} L ${westArrowX + 20},${westArrowY + 4} L ${westArrowX + 20},${westArrowY + 10} Z`} fill="#333333" stroke="#333333" strokeWidth="1" />);

                const westTurnX = centerX + intersectionOffset + 150;
                const westTurnYOffset = laneWidth * 1.8;
                const westTurnArrowY = centerY - westTurnYOffset;
                const westTextY = westTurnArrowY - 25;
                elements.push(
                    <g key="west-left-turn" transform={`translate(${westTurnX + 75}, ${westTurnArrowY})`}>
                        <LeftTurnArrow rotation={270} size={35} />
                    </g>
                );
                elements.push(
                    <text key="west-left-count" x={westTurnX + 75} y={westTextY} textAnchor="start" fontSize="14" fontWeight="bold" fill="#2c3e50" transform={`rotate(-90, ${westTurnX + 75}, ${westTextY})`}>
                        {westLeftCount}
                    </text>
                );
                // Straight
                elements.push(
                    <g key="west-straight-turn" transform={`translate(${westTurnX + 25}, ${westTurnArrowY})`}>
                        <StraightArrow rotation={270} size={35} />
                    </g>
                );
                elements.push(
                    <text key="west-straight-count" x={westTurnX + 25} y={westTextY} textAnchor="start" fontSize="14" fontWeight="bold" fill="#2c3e50" transform={`rotate(-90, ${westTurnX + 25}, ${westTextY})`}>
                        {westStraightCount}
                    </text>
                );
                // Right turn
                elements.push(
                    <g key="west-right-turn" transform={`translate(${westTurnX - 25}, ${westTurnArrowY})`}>
                        <RightTurnArrow rotation={270} size={35} />
                    </g>
                );
                elements.push(
                    <text key="west-right-count" x={westTurnX - 25} y={westTextY} textAnchor="start" fontSize="14" fontWeight="bold" fill="#2c3e50" transform={`rotate(-90, ${westTurnX - 25}, ${westTextY})`}>
                        {westRightCount}
                    </text>
                );
                // U-turn
                elements.push(
                    <g key="west-uturn" transform={`translate(${westTurnX - 75}, ${westTurnArrowY})`}>
                        <UTurnArrow rotation={270} size={35} />
                    </g>
                );
                elements.push(
                    <text key="west-uturn-count" x={westTurnX - 75} y={westTextY} textAnchor="start" fontSize="14" fontWeight="bold" fill="#2c3e50" transform={`rotate(-90, ${westTurnX - 75}, ${westTextY})`}>
                        {westUTurnCount}
                    </text>
                );

                const westOutArrowX = centerX + intersectionOffset + 80;
                const westOutArrowY = centerY + laneWidth;
                elements.push(<path key="arrow-west-out" d={`M ${westOutArrowX + 30},${westOutArrowY} L ${westOutArrowX + 10},${westOutArrowY - 10} L ${westOutArrowX + 10},${westOutArrowY - 4} L ${westOutArrowX},${westOutArrowY - 4} L ${westOutArrowX},${westOutArrowY + 4} L ${westOutArrowX + 10},${westOutArrowY + 4} L ${westOutArrowX + 10},${westOutArrowY + 10} Z`} fill="#dc2626" stroke="#dc2626" strokeWidth="1" />);
                elements.push(
                    <text key="west-out-label" x={westOutArrowX + 45} y={westOutArrowY + 5} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#dc2626" transform={`rotate(-90, ${westOutArrowX + 45}, ${westOutArrowY + 5})`}>
                        {westOutCount}
                    </text>
                );

                elements.push(
                    <text key="west-street-name" x={centerX + intersectionOffset + roadLength + 10} y={centerY} textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333333" transform={`rotate(90, ${centerX + intersectionOffset + roadLength + 10}, ${centerY})`}>
                        {westStreet}
                    </text>
                );

                elements.push(
                    <text key="west-cardinal" x={centerX + intersectionOffset + roadLength - 20} y={centerY - laneWidth} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333333">
                        {westCardinal}
                    </text>
                );
            }
        }

        return elements;
    };

    return (
        <div className="w-full">
            <div className={`flex justify-center bg-gray-50 rounded ${compact ? "p-1" : "p-6"} print:p-0`}>
                <svg
                    {...(compact ? {} : { width, height })}
                    style={compact ? { width: "100%", height: "auto" } : undefined}
                    viewBox={`0 0 ${width} ${height}`}
                    className="border border-gray-300 bg-white rounded shadow print:border-0 print:shadow-none print:max-w-full print:h-auto print:w-[90%]"
                >
                    <rect x={centerX - intersectionSize / 2} y={centerY - intersectionSize / 2} width={intersectionSize} height={intersectionSize} fill="white" stroke="#333333" strokeWidth="2" />
                    <g transform={`rotate(${isDiagonal ? 45 : 0}, ${centerX}, ${centerY})`}>
                        {renderRoad(true)}
                        {renderRoad(false)}
                    </g>
                </svg>
            </div>

            {!compact && (
            <div className="mt-4 p-4 bg-white border border-gray-300 rounded">
                <h4 className="text-sm font-semibold mb-3">Legend</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                        <strong>Black arrows:</strong> Incoming traffic to intersection
                    </div>
                    <div>
                        <strong>Red arrows:</strong> Outgoing traffic from intersection
                    </div>
                    <div>
                        <strong>Daily Total:</strong> Total vehicles for the entire day
                    </div>
                    <div>
                        <strong>(Hourly):</strong> Vehicles during selected hour (shown in parentheses)
                    </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600">
                    <strong>Example:</strong> "125 (18)" means 125 vehicles total for the day, with 18 during the selected hour
                </div>
            </div>
            )}
        </div>
    );
};
